/**
 * Shared config for the customer-stories platform sub-filter.
 *
 * The blog renders a secondary platform tablist when a category has
 * `additionalFilters` containing "platform" (e.g. customer-stories), and the
 * customer-stories case-studies grid renders the same platforms as tabs. Each
 * platform value maps to the article `platform` field.
 *
 * Kept free of server-only imports so it can be used from client components.
 */

import type { Where } from "payload";
import {
  CUSTOMER_STORIES_PLATFORMS,
  CUSTOMER_STORIES_PLATFORM_LABELS,
  type CustomerStoriesPlatform,
} from "@mw/shared/customerStoriesPlatforms";

export const PLATFORM_ADDITIONAL_FILTER = "platform" as const;

/** Literal URL segment for the platform sub-filter: /blog/category/{slug}/platform/{platform} */
export const PLATFORM_PATH_SEGMENT = "platform" as const;

/** The category whose platform sub-filter scopes the customer-stories articles. */
export const CUSTOMER_STORIES_CATEGORY_SLUG = "customer-stories" as const;
export { CUSTOMER_STORIES_PLATFORMS, CUSTOMER_STORIES_PLATFORM_LABELS };
export type { CustomerStoriesPlatform };

export function isValidPlatform(value: string | undefined | null): value is CustomerStoriesPlatform {
  return !!value && (CUSTOMER_STORIES_PLATFORMS as readonly string[]).includes(value);
}

export function categoryHasPlatformFilter(additionalFilters: ("platform" | string)[] | null | undefined): boolean {
  return !!additionalFilters?.includes(PLATFORM_ADDITIONAL_FILTER);
}

/**
 * Builds the article `where` clause for a platform-filtered category listing:
 * the category slug, plus the platform when one is selected.
 */
export function buildPlatformCategoryWhere(categorySlug: string, platform?: string): Where {
  if (isValidPlatform(platform)) {
    return {
      "category.slug": { equals: categorySlug },
      platform: { equals: platform },
      slug: { exists: true },
    };
  }

  return {
    "category.slug": { equals: categorySlug },
    slug: { exists: true },
  };
}
